import React, { useState, useEffect, useRef } from 'react';
import { Bot, Sparkles, ShieldCheck, MessageSquare, Users, Database, Rocket, BadgeCheck, Eye, Calendar, TrendingUp, Clock, Quote, ArrowUpRight, Star, UsersRound, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import ProjectQuestionForm from '../ProjectQuestionForm';

const Feature = ({ icon: Icon, title, description }) => (
	<div className="p-6 bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow">
		<div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center mb-4">
			<Icon className="w-5 h-5" />
		</div>
		<h3 className="text-lg font-semibold mb-2">{title}</h3>
		<p className="text-gray-600 text-sm leading-relaxed">{description}</p>
	</div>
);

const Step = ({ index, title, description }) => (
	<div className="flex items-start space-x-4">
		<div className="flex-shrink-0">
			<div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">
				{index}
			</div>
		</div>
		<div>
			<h4 className="font-semibold mb-1">{title}</h4>
			<p className="text-gray-600 text-sm">{description}</p>
		</div>
	</div>
);

const AIDetailPage = () => {
	const [pageContent, setPageContent] = useState(null);
	const [loading, setLoading] = useState(true);
	const [contentReady, setContentReady] = useState(false);
	const [showDemoForm, setShowDemoForm] = useState(false);
	
	// Sticky cards effect için ref - Hook'lar conditional return'lerden ÖNCE olmalı
	const stickyCardsRef = useRef([]);

	const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';

	useEffect(() => {
		const fetchPageContent = async () => {
			try {
				const res = await fetch(`${apiBase}/backend/admin_api.php?action=getPageContent&slug=yapay-zeka`);
				const data = await res.json();
				
				if (data.status === 'success') {
					setPageContent(data.data);
					setTimeout(() => setContentReady(true), 100);
				}
			} catch (error) {
				console.error('İçerik yükleme hatası:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchPageContent();
	}, [apiBase]);

	// Sticky cards scroll effect - Hook'lar conditional return'lerden ÖNCE olmalı
	// pageContent null olabilir, bu yüzden optional chaining kullanıyoruz
	const additionalSections = pageContent?.additional_sections || {};
	const successStories = additionalSections?.success_stories || {};
	const heroFeatures = additionalSections?.hero_features || [];
	
	useEffect(() => {
		if (!successStories?.cards || successStories.cards.length === 0) return;
		
		const handleScroll = () => {
			const cards = stickyCardsRef.current;
			cards.forEach((card, index) => {
				if (!card) return;
				const nextCard = cards[index + 1];
				if (!nextCard) return;
				
				const cardRect = card.getBoundingClientRect();
				const nextCardRect = nextCard.getBoundingClientRect();
				
				if (nextCardRect.top < cardRect.bottom) {
					card.classList.add('scaled');
				} else {
					card.classList.remove('scaled');
				}
			});
		};
		
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, [successStories]);

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
			</div>
		);
	}

	if (!pageContent) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<p className="text-gray-600">İçerik yüklenemedi.</p>
			</div>
		);
	}

	const iconMap = {
		'Sparkles': Sparkles,
		'Eye': Eye,
		'MessageSquare': MessageSquare,
		'Users': Users,
		'Database': Database,
		'ShieldCheck': ShieldCheck,
		'BadgeCheck': BadgeCheck,
		'Rocket': Rocket,
		'Bot': Bot
	};

	const HeroBadgeIcon = iconMap[pageContent.hero_badge_icon] || Sparkles;
	const Button1Icon = iconMap[pageContent.hero_button1_icon] || Eye;
	const Button2Icon = iconMap[pageContent.hero_button2_icon] || MessageSquare;

	const features = additionalSections.features_section || {};
	const howItWorks = additionalSections.how_it_works_section || {};
	const cta = additionalSections.cta || {};
	const testimonials = additionalSections.testimonials || {};

	return (
		<div className="">
			{/* Hero - Müşteri Destek AI Chatbot */}
			<section className="bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600 text-white">
				<div className="container mx-auto px-6 py-16">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
						<div style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.5s ease-in' }}>
							<h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-4">
								{pageContent.hero_title ? (
									<span dangerouslySetInnerHTML={{ __html: pageContent.hero_title.replace(/<br\/>/g, '<br/>') }} />
								) : (
									<>Müşteri Destek <br/>AI Chatbot</>
								)}
							</h1>
							<p className="text-white/90 text-base md:text-lg mb-6">
								{pageContent.hero_description || "Destek taleplerinin %70'ini anında çözün ve ekibiniz ve müşterileriniz için zamandan kazanın."}
							</p>
							<div className="flex flex-wrap items-center gap-3 mb-6">
								<button 
									onClick={() => setShowDemoForm(true)} 
									className="px-5 py-3 bg-white text-indigo-700 font-semibold rounded-lg hover:bg-gray-50 flex items-center space-x-2"
								>
									<Button1Icon className="w-4 h-4" />
									<span>Demo Talebinde Bulun</span>
								</button>
								<a href={pageContent.hero_button2_link || '/iletisim'} className="px-5 py-3 bg-white/15 text-white font-semibold rounded-lg hover:bg-white/20 flex items-center space-x-2">
									<Button2Icon className="w-4 h-4" />
									<span>{pageContent.hero_button2_text || 'İletişime Geç'}</span>
								</a>
							</div>
							{/* Özellikler Listesi - Veritabanından */}
							{heroFeatures.length > 0 && (
								<ul className="space-y-3 mb-6">
									{heroFeatures.map((feature, index) => {
										const text = feature.text || feature;
										// Bold kısmı varsa vurgula
										const boldText = feature.bold || '';
										let displayText = text;
										if (boldText && text.includes(boldText)) {
											displayText = text.replace(boldText, `<strong>${boldText}</strong>`);
										}
										return (
											<li key={index} className="flex items-start gap-3 text-white/90">
												<CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
												<span dangerouslySetInnerHTML={{ __html: displayText }} />
											</li>
										);
									})}
								</ul>
							)}
						</div>
						<div style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.5s ease-in 0.2s' }}>
							{pageContent.hero_image_url ? (
								<div className="relative overflow-hidden rounded-2xl shadow-2xl">
									{pageContent.hero_image_url.match(/\.(mp4|webm|ogg|avi|mov|wmv|mkv|flv)$/i) ? (
										<video 
											src={pageContent.hero_image_url} 
											className="w-full h-full object-cover"
											autoPlay
											loop
											muted
											playsInline
										/>
									) : (
										<img 
											src={pageContent.hero_image_url} 
											alt={pageContent.hero_title} 
											className="w-full h-full object-cover"
										/>
									)}
								</div>
							) : (
								<div className="relative overflow-hidden rounded-2xl shadow-2xl">
									<div className="aspect-video bg-black/20 backdrop-blur-sm flex items-center justify-center">
										<div className="flex flex-col items-center justify-center">
											<div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center mb-4">
												<Bot className="w-12 h-12 text-white" />
											</div>
											<p className="text-white/90 font-semibold">Akıllı Diyalog Motoru</p>
											<p className="text-white/70 text-sm">(Görsel eklemek için admin paneli kullanın)</p>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</section>

		{/* Gogi AI ile Neler Mümkün Keşfedin - Video Section */}
		<section className="bg-gray-50 py-16" style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.5s ease-in 0.3s' }}>
			<div className="container mx-auto px-6">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
					<div className="relative rounded-2xl overflow-hidden shadow-xl">
						{features.video_url ? (
							<video 
								autoPlay 
								loop 
								muted 
								playsInline
								className="w-full h-full object-cover"
								poster={features.video_poster || ''}
							>
								<source src={features.video_url} type="video/mp4" />
								<source src={features.video_url.replace('.mp4', '.webm')} type="video/webm" />
							</video>
						) : (
							<div className="aspect-video bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
								<Bot className="w-24 h-24 text-purple-400" />
							</div>
						)}
					</div>
					<div>
						<div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm mb-4">
							<Calendar className="w-4 h-4 text-purple-600" />
							<span className="text-sm font-medium text-gray-700">Demo Talep Et</span>
						</div>
						<h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-gray-900">
							Gogi AI ile Neler Mümkün Keşfedin
						</h2>
						<p className="text-gray-600 text-lg mb-6">
							Güçlü AI destekli otomasyon, kişiselleştirilmiş etkileşim, sorunsuz müşteri temsilcisi devralma süreci ve akıllı içgörülerle müşteri desteğinizi anında geliştirin. — İşletmenize mükemmel uyum sağlamak ve destek operasyonlarınızı optimize etmek için geliştirildi.
						</p>
						<a href="/iletisim" className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors">
							<span>Kişiselleştirilmiş Demo Planlayın</span>
							<ArrowUpRight className="w-4 h-4" />
						</a>
					</div>
				</div>
			</div>
		</section>

		{/* Özellikler */}
		<section id="ozellikler" className="container mx-auto px-6 py-16" style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.5s ease-in 0.4s' }}>
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
				<div>
					<h2 className="text-2xl md:text-3xl font-extrabold mb-3">
						{features.title || 'Neler Sunuyor?'}
					</h2>
					<p className="text-gray-600 mb-8">
						{features.subtitle || features.description || 'Gogi AI ile müşteri deneyimini ileri taşıyın.'}
					</p>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
						{(features.cards || features.items || []).map((feature, index) => (
							<Feature 
								key={index}
								icon={iconMap[feature.icon] || MessageSquare} 
								title={feature.title} 
								description={feature.description} 
							/>
						))}
					</div>
				</div>
				<div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg overflow-hidden">
					{features.image_url && features.image_url.match(/\.(mp4|webm|ogg|avi|mov|wmv|mkv|flv)$/i) ? (
						<video 
							src={features.image_url} 
							className="w-full h-full object-cover"
							autoPlay
							loop
							muted
							playsInline
						/>
					) : (
						<img 
							src={features.image_url || 'https://cdn.natrocdn.com/ResourceFiles/v1/images/GogiAI/gogi-features.png'} 
							alt={features.title || 'Gogi AI Neler Sunuyor?'} 
							className="w-full h-full object-cover"
						/>
					)}
				</div>
			</div>
		</section>

			{/* Başarı Hikayeleri - Success Stories */}
			{(successStories.cards && successStories.cards.length > 0) && (
				<section className="bg-white py-16" style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.5s ease-in 0.5s' }}>
					<div className="container mx-auto px-6">
						<div className="text-center mb-12">
							<div className="inline-flex items-center gap-2 mb-4">
								<UsersRound className="w-5 h-5 text-purple-600" />
								<span className="text-sm font-semibold text-purple-600 uppercase tracking-wide">Başarı Hikayeleri</span>
							</div>
							<h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
								Önde Gelen Markalar Gogi AI ile Müşteri Destekte Nasıl Öne Geçiyor?
							</h2>
						</div>
						<div className="relative">
							<style>{`
								.success-story-card {
									position: sticky;
									top: 20px;
									transition: transform 0.3s ease;
								}
								.success-story-card.scaled {
									transform: scale(0.95);
								}
							`}</style>
							<div className="space-y-8">
								{successStories.cards.map((story, index) => (
									<div
										key={index}
										ref={el => stickyCardsRef.current[index] = el}
										className="success-story-card"
									>
										<Card className="overflow-hidden shadow-xl">
											<div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
												<div className="relative h-64 lg:h-auto">
													<img 
														src={story.image || 'https://via.placeholder.com/800x600'} 
														alt={story.company || 'Success Story'} 
														className="w-full h-full object-cover"
													/>
													{story.logo && (
														<div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-lg">
															<img src={story.logo} alt={story.company} className="h-8 w-auto" />
														</div>
													)}
												</div>
												<CardContent className="p-8">
													<div className="mb-6">
														<h3 className="text-2xl font-bold text-gray-900 mb-4">
															{story.title || 'Başarı Hikayesi'}
														</h3>
														{story.stat && (
															<div className="flex items-center gap-3 mb-4">
																<div className="text-3xl font-bold text-purple-600">{story.stat.value}</div>
																{story.stat.icon === 'TrendingUp' && <TrendingUp className="w-5 h-5 text-purple-600" />}
																{story.stat.icon === 'Clock' && <Clock className="w-5 h-5 text-purple-600" />}
																{story.stat.icon === 'Users' && <Users className="w-5 h-5 text-purple-600" />}
															</div>
														)}
														{story.stat_description && (
															<p className="text-gray-600">{story.stat_description}</p>
														)}
													</div>
													<div className="border-t pt-6">
														<div className="flex items-start gap-3 mb-4">
															<Quote className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
															<p className="text-gray-700 italic leading-relaxed">
																{story.testimonial || story.quote}
															</p>
														</div>
														{story.author && (
															<div className="flex items-center gap-3 mt-4">
																{story.author_image && (
																	<img 
																		src={story.author_image} 
																		alt={story.author.name} 
																		className="w-12 h-12 rounded-full object-cover"
																	/>
																)}
																<div>
																	<div className="font-semibold text-gray-900">{story.author.name}</div>
																	<div className="text-sm text-gray-600">{story.author.role}</div>
																</div>
															</div>
														)}
													</div>
												</CardContent>
											</div>
										</Card>
									</div>
								))}
							</div>
						</div>
					</div>
				</section>
			)}

			{/* Kullanıcı Yorumları - Testimonials */}
			{(testimonials.cards && testimonials.cards.length > 0) && (
				<section className="bg-gray-50 py-16" style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.5s ease-in 0.6s' }}>
					<div className="container mx-auto px-6">
						<div className="text-center mb-12">
							<div className="inline-flex items-center gap-2 mb-4">
								<Star className="w-5 h-5 text-purple-600" />
								<span className="text-sm font-semibold text-purple-600 uppercase tracking-wide">Kullanıcı Yorumları</span>
							</div>
							<h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
								Destek ekipleri ve işletmeler Gogi AI hakkında ne düşünüyor?
							</h2>
						</div>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
							{testimonials.cards.map((testimonial, index) => (
								<Card key={index} className="shadow-lg hover:shadow-xl transition-shadow">
									<CardHeader>
										<div className="flex items-start justify-between">
											<div className="flex items-center gap-3">
												{testimonial.author_image && (
													<img 
														src={testimonial.author_image} 
														alt={testimonial.author?.name || 'User'} 
														className="w-12 h-12 rounded-full object-cover"
													/>
												)}
												<div>
													<div className="font-semibold text-gray-900">{testimonial.author?.name || 'Kullanıcı'}</div>
													<div className="text-sm text-gray-600">{testimonial.author?.role || ''}</div>
													{testimonial.author?.company && (
														<div className="text-xs text-gray-500">{testimonial.author.company}</div>
													)}
												</div>
											</div>
											{testimonial.verified && (
												<Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
											)}
										</div>
									</CardHeader>
									<CardContent>
										<div className="border-t pt-4">
											<p className="text-gray-700 leading-relaxed">
												{testimonial.text || testimonial.content}
											</p>
										</div>
									</CardContent>
								</Card>
							))}
						</div>
					</div>
				</section>
			)}

			{/* Çalışma Şekli */}
			<section className="bg-gray-50" style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.5s ease-in 0.7s' }}>
				<div className="container mx-auto px-6 py-16">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
						<div>
							<h3 className="text-2xl font-extrabold mb-4">
								{howItWorks.title || 'Nasıl Çalışır?'}
							</h3>
							<div className="space-y-5">
								{(howItWorks.steps || []).map((step, index) => (
									<Step 
										key={index}
										index={(step.index || (index + 1))} 
										title={step.title} 
										description={step.description} 
									/>
								))}
							</div>
						</div>
						<div className="p-6 bg-white rounded-2xl border shadow-sm">
							<div className="flex items-center space-x-3 mb-4">
								<Bot className="w-5 h-5 text-indigo-600" />
								<p className="font-semibold">Başlıca Yetkinlikler</p>
							</div>
							<ul className="list-disc list-inside text-gray-700 space-y-2 text-sm">
								{(howItWorks.capabilities || []).map((capability, index) => (
									<li key={index}>{capability}</li>
								))}
							</ul>
						</div>
					</div>
				</div>
			</section>

		{/* CTA */}
		<section className="container mx-auto px-6 py-16" style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.5s ease-in 0.5s' }}>
			<div className="rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 p-8 md:p-12 text-white flex flex-col md:flex-row items-center justify-between">
				<div className="mb-6 md:mb-0">
					<h3 className="text-2xl md:text-3xl font-extrabold mb-2">
						{cta.title || 'Gogi AI ile tanışın'}
					</h3>
					<p className="text-white/90">
						{cta.description || 'Sitenize akıllı bir asistan eklemek için bizimle iletişime geçin.'}
					</p>
				</div>
				<div>
					<a href={cta.button_link || '/iletisim'} className="px-5 py-3 bg-white text-indigo-700 font-semibold rounded-lg hover:bg-gray-50 inline-block">
						{cta.button_text || 'İletişime Geç'}
					</a>
				</div>
			</div>
		</section>

		{/* Demo Form Modal */}
		{showDemoForm && (
			<div className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4">
				<div className="relative z-50 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
					<ProjectQuestionForm 
						project={{ name: 'Yapay zeka', id: 'gogi-ai' }}
						onClose={() => setShowDemoForm(false)}
						formTitle="Gogi AI Demo Talebi"
						projectLabel="Yapay zeka"
						showQuestion={false}
					/>
				</div>
			</div>
		)}
		</div>
	);
};

export default AIDetailPage;


